import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../../ui/Modal';
import { Icon, Sparkles } from '../../ui/Icon';
import { toast } from '../../ui/Toast';
import { withinLimit } from '../../../utils/uploadLimits';
import { ATTACHMENT_ACCEPT } from './documentFiles';

export function AddWithAiButton({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-teal-200 dark:border-teal-800 bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-300 hover:bg-teal-100 dark:hover:bg-teal-900/40 text-xs font-semibold transition-colors cursor-pointer shrink-0"
    >
      <Icon as={Sparkles} size={14} /> <span className="hidden sm:inline">{t('bookings.form.addWithAi')}</span>
    </button>
  );
}

/** File picker that hands the chosen tickets to a form for AI extraction. */
export function AddWithAiModal({ open, onClose, onFiles }: {
  open: boolean;
  onClose: () => void;
  onFiles: (files: File[]) => void;
}) {
  const { t } = useTranslation();
  const [dragOver, setDragOver] = useState(false);

  function handleFiles(files: FileList | File[] | null) {
    if (!files || files.length === 0) return;
    const { accepted, errors } = withinLimit(Array.from(files), 'media');
    errors.forEach(message => toast(message, 'error'));
    if (accepted.length > 0) onFiles(accepted);
  }

  return (
    <Modal open={open} onClose={onClose} title={t('bookings.form.addWithAiTitle')} size="sm">
      <div className="space-y-4">
        <p className="text-sm text-slate-500 dark:text-slate-400">
          {t('bookings.form.addWithAiExplanation')}
        </p>
        <label
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={e => {
            e.preventDefault();
            setDragOver(false);
            handleFiles(e.dataTransfer.files);
          }}
          className={`flex flex-col items-center justify-center gap-2 py-10 px-4 rounded-xl border-2 border-dashed cursor-pointer transition-colors text-center ${
            dragOver
              ? 'border-teal-400 bg-teal-50 dark:bg-teal-900/20'
              : 'border-slate-200 dark:border-slate-700 hover:border-teal-300 dark:hover:border-teal-600 hover:bg-teal-50/50 dark:hover:bg-teal-900/10'
          }`}
        >
          <input
            type="file"
            multiple
            accept={ATTACHMENT_ACCEPT}
            className="hidden"
            onChange={e => handleFiles(e.target.files)}
          />
          <Icon as={Sparkles} size={22} className="text-teal-500" />
          <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">{t('bookings.form.addWithAiDropTitle')}</p>
          <p className="text-xs text-slate-400 dark:text-slate-500">{t('bookings.form.addWithAiDropSubtitle')}</p>
        </label>
      </div>
    </Modal>
  );
}
